import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { scrollToElement } from "@sia/style-guide";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { MasterKeyContext } from "contexts/MasterKeyContext";
import { CryptoInitializationError } from "common/crypto";
import surveyAdapter from "common/Adapters/SurveyAdapter/Router";
import { useEmployeeImport } from "./useEmployeeImport";
import { createFailedEmployee, getEmployeeId, hasEmployeeErrors, isSameEmployee, upsertEmployeeInList } from "./utils";

export function useEmployees() {
  const { cryptoSessionRevision } = useContext(MasterKeyContext);
  const {
    employees,
    setCollectionData,
    companyId,
    companyInputId,
    setShowEmployeeEntryForm,
    employeeEntryFormItem,
    setEmployeeEntryFormItem,
    setShowEmployeesFileUpload,
    dropdownOptionsEmployees,
  } = useContext(DataCollectionContext);

  const [employeeFormDirty, setEmployeeFormDirty] = useState(false);
  const [employeeFormVersion, setEmployeeFormVersion] = useState(0);
  const [showDeleteEmployeeModal, setShowDeleteEmployeeModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [requiresEmployeeReload, setRequiresEmployeeReload] = useState(false);
  const employeesRef = useRef(employees);
  const entryItemRef = useRef(employeeEntryFormItem);
  const saveRef = useRef(null);
  const deleteRef = useRef(null);
  const employeeUiScope = `${cryptoSessionRevision}:${companyInputId ?? ""}`;
  const employeeUiScopeRef = useRef(employeeUiScope);
  employeesRef.current = employees;
  entryItemRef.current = employeeEntryFormItem;
  employeeUiScopeRef.current = employeeUiScope;

  const getEmployeeUiScope = useCallback(() => employeeUiScopeRef.current, []);
  const replaceEmployees = useCallback(updater => {
    const next = updater(employeesRef.current || []);
    employeesRef.current = next;
    setCollectionData(state => ({ ...state, employees: next }));
    return next;
  }, [setCollectionData]);

  const setEntryForm = useCallback((employee, dirty = false) => {
    const open = employee !== null;
    setEmployeeFormDirty(open && dirty);
    setEmployeeEntryFormItem(open ? employee || {} : {});
    setShowEmployeeEntryForm(open);
    setEmployeeFormVersion(version => version + 1);
  }, [setEmployeeEntryFormItem, setShowEmployeeEntryForm]);

  const onRequiresReload = useCallback((expectedScope = getEmployeeUiScope()) => {
    if (getEmployeeUiScope() === expectedScope) setRequiresEmployeeReload(true);
  }, [getEmployeeUiScope]);

  const persistEmployee = useCallback(async (
    employeeInput,
    expectedCryptoRevision = cryptoSessionRevision,
    expectedUiScope = getEmployeeUiScope()
  ) => {
    try {
      const result = await surveyAdapter.upsertEmployee({
        employee: employeeInput,
        companyId,
        companyInputId,
        dropdownOptions: dropdownOptionsEmployees,
        employees: employeesRef.current,
        expectedCryptoRevision,
      });
      surveyAdapter.assertCryptoSessionRevision(expectedCryptoRevision);
      const employee = result?.employee || employeeInput;
      const ok = result?.ok === true || result?.isSuccess === true;
      if (result?.requiresReload) onRequiresReload(expectedUiScope);
      return ok || hasEmployeeErrors(employee)
        ? { ...result, ok, employee }
        : { ...result, ok: false, employee: createFailedEmployee(employee, result?.error) };
    } catch (error) {
      const requiresReload = error instanceof CryptoInitializationError;
      if (requiresReload) onRequiresReload(expectedUiScope);
      return { ok: false, employee: createFailedEmployee(employeeInput, error), error, requiresReload };
    }
  }, [companyId, companyInputId, cryptoSessionRevision, dropdownOptionsEmployees, getEmployeeUiScope, onRequiresReload]);

  const isCrudBusy = useCallback(() => Boolean(saveRef.current || deleteRef.current), []);
  const isMutationBlocked = useCallback(() => requiresEmployeeReload || isCrudBusy(), [isCrudBusy, requiresEmployeeReload]);
  const { isImportingEmployees, isImportActive, importEmployees } = useEmployeeImport({
    employeesRef,
    replaceEmployees,
    persistEmployee,
    isMutationBlocked,
    onRequiresReload,
    cryptoSessionRevision,
    getUiScope: getEmployeeUiScope,
  });

  useEffect(() => {
    setEmployeeFormDirty(false);
    setEmployeeFormVersion(version => version + 1);
    setEmployeeToDelete(null);
    setShowDeleteEmployeeModal(false);
    setRequiresEmployeeReload(false);
    setEmployeeEntryFormItem({});
    setShowEmployeesFileUpload(false);
  }, [employeeUiScope, setEmployeeEntryFormItem, setShowEmployeesFileUpload]);

  const hasRunningOperation = useCallback(() => isCrudBusy() || isImportActive(), [isCrudBusy, isImportActive]);
  const cannotMutate = useCallback(() => requiresEmployeeReload || hasRunningOperation(), [hasRunningOperation, requiresEmployeeReload]);

  const discardEmployeeFormChanges = useCallback(() => {
    if (hasRunningOperation()) return false;
    setEntryForm(null);
    return true;
  }, [hasRunningOperation, setEntryForm]);

  const openFileUpload = useCallback(() => {
    if (cannotMutate()) return false;
    setShowEmployeesFileUpload(true);
    setEntryForm(null);
    return true;
  }, [cannotMutate, setEntryForm, setShowEmployeesFileUpload]);

  const openEntryForm = useCallback((providedEmployee, employeeId) => {
    if (cannotMutate()) return false;
    const isNew = employeeId === "blank";
    const target = isNew
      ? {}
      : providedEmployee && Object.keys(providedEmployee).length
      ? providedEmployee
      : employeesRef.current.find(employee => isSameEmployee(employee, employeeId));
    if (!isNew && !target) {
      console.error(`Employee "${employeeId}" was not found.`);
      return false;
    }
    setShowEmployeesFileUpload(false);
    setEntryForm(target, false);
    return true;
  }, [cannotMutate, setEntryForm, setShowEmployeesFileUpload]);

  const addOrUpdate = useCallback(employeeInput => {
    if (saveRef.current) return saveRef.current;
    if (requiresEmployeeReload || deleteRef.current || isImportActive()) {
      const failed = createFailedEmployee(employeeInput, new Error(requiresEmployeeReload ? "Reload the survey before changing employees again." : "Another employee operation is still in progress."));
      setEntryForm(failed, true);
      return Promise.resolve(failed);
    }

    const input = { ...(entryItemRef.current || {}), ...(employeeInput || {}), errors: {} };
    const operationScope = getEmployeeUiScope();
    const expectedCryptoRevision = cryptoSessionRevision;
    setPendingAction("save");
    const operation = (async () => {
      const result = await persistEmployee(input, expectedCryptoRevision, operationScope);
      const saved = result.employee;
      if (getEmployeeUiScope() !== operationScope) return saved;
      if (!result.ok || hasEmployeeErrors(saved)) {
        setEntryForm(saved, true);
        return saved;
      }
      try {
        replaceEmployees(current => upsertEmployeeInList(current, saved));
      } catch (error) {
        const failed = { ...createFailedEmployee(saved, error), persistenceUncertain: true };
        onRequiresReload(operationScope);
        setEntryForm(failed, true);
        return failed;
      }
      setEntryForm(null);
      scrollToElement("section-scroll-anchor");
      return saved;
    })();

    saveRef.current = operation.finally(() => {
      saveRef.current = null;
      setPendingAction(null);
    });
    return saveRef.current;
  }, [cryptoSessionRevision, getEmployeeUiScope, isImportActive, onRequiresReload, persistEmployee, replaceEmployees, requiresEmployeeReload, setEntryForm]);

  const deleteEmployee = useCallback(id => {
    if (cannotMutate()) return false;
    const employee = employeesRef.current.find(item => isSameEmployee(item, id));
    if (!employee) return false;
    setEmployeeToDelete(employee);
    setShowDeleteEmployeeModal(true);
    return true;
  }, [cannotMutate]);

  const onCancelEmployeeConfirm = useCallback(() => {
    if (hasRunningOperation()) return false;
    setEmployeeToDelete(null);
    setShowDeleteEmployeeModal(false);
    return true;
  }, [hasRunningOperation]);

  const onDeleteEmployeeConfirm = useCallback(() => {
    if (deleteRef.current) return deleteRef.current;
    if (requiresEmployeeReload || saveRef.current || isImportActive()) return Promise.resolve(false);
    const employeeId = getEmployeeId(employeeToDelete);
    if (employeeId == null) return Promise.resolve(false);
    const operationScope = getEmployeeUiScope();
    const expectedCryptoRevision = cryptoSessionRevision;
    setPendingAction("delete");
    const operation = (async () => {
      try {
        const result = await surveyAdapter.deleteEmployee(employeeId, expectedCryptoRevision);
        if (getEmployeeUiScope() !== operationScope || result?.ok !== true) return false;
        replaceEmployees(current => current.filter(employee => !isSameEmployee(employee, employeeId)));
        if (isSameEmployee(entryItemRef.current, employeeId)) setEntryForm(null);
        setEmployeeToDelete(null);
        setShowDeleteEmployeeModal(false);
        return true;
      } catch (error) {
        if (getEmployeeUiScope() === operationScope && error instanceof CryptoInitializationError) onRequiresReload(operationScope);
        console.error("Employee deletion failed.", error);
        return false;
      }
    })();
    deleteRef.current = operation.finally(() => {
      deleteRef.current = null;
      setPendingAction(null);
    });
    return deleteRef.current;
  }, [cryptoSessionRevision, employeeToDelete, getEmployeeUiScope, isImportActive, onRequiresReload, replaceEmployees, requiresEmployeeReload, setEntryForm]);

  return {
    employeeFormDirty,
    setEmployeeFormDirty,
    employeeFormVersion,
    showDeleteEmployeeModal,
    employeeToDelete,
    isImportingEmployees,
    isEmployeeMutationPending: pendingAction !== null || isImportingEmployees || requiresEmployeeReload || Boolean(saveRef.current || deleteRef.current),
    addOrUpdate,
    deleteEmployee,
    onCancelEmployeeConfirm,
    onDeleteEmployeeConfirm,
    openEntryForm,
    openFileUpload,
    importEmployees,
    discardEmployeeFormChanges,
  };
}
